import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { usersService, User, CreateUserInput, UpdateUserInput, ListUsersParams } from '@/services/api/users';
import { departmentsService } from '@/services/api/departments';
import { useAuthStore } from '@/store/authStore';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Search,
  Pencil,
  Trash2,
  Users,
  Plus,
  UserCircle,
  Mail,
  Shield,
  Building2,
  Phone,
  Briefcase,
  Clock,
  Calendar,
  X,
  Save,
  CheckCircle,
  XCircle,
  AlertCircle,
  KeyRound,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

interface FormData extends CreateUserInput {
  status?: 'active' | 'inactive' | 'suspended' | 'pending';
}

const initialFormData: FormData = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  role: 'user',
  phone: '',
  jobTitle: '',
  departmentIds: [],
};

export function UsersPage() {
  const queryClient = useQueryClient();
  const { user: currentUser, setUser: setCurrentUser } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [params] = useState<ListUsersParams>({
    limit: 50,
    offset: 0,
    sortBy: 'created_at',
    sortOrder: 'desc',
  });

  // Fetch users
  const { data, isLoading, error } = useQuery({
    queryKey: ['users', params, searchQuery, statusFilter, roleFilter],
    queryFn: () =>
      usersService.listUsers({
        ...params,
        search: searchQuery || undefined,
        status: statusFilter || undefined,
        role: roleFilter || undefined,
      }),
    staleTime: 0,
    gcTime: 0,
  });

  // Fetch departments for assignment
  const { data: departmentsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsService.listDepartments(),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (input: CreateUserInput) => usersService.createUser(input),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User created successfully', {
        description: `Account for ${formData.email} is now active.`,
      });
      setIsCreating(false);
      setFormData(initialFormData);
      if (response) {
        setSelectedUser(response);
      }
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to create user';
      toast.error('Failed to create user', { description: message });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserInput }) =>
      usersService.updateUser(id, input),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User updated successfully');
      setIsEditing(false);
      if (response) {
        setSelectedUser(response);

        // If the updated user is the current logged-in user, update the auth store
        const isCurrentUser = currentUser && (
          response.id === currentUser.id ||
          response.cognitoUserId === currentUser.cognitoUserId ||
          response.email === currentUser.email
        );
        if (isCurrentUser) {
          setCurrentUser({
            ...currentUser,
            firstName: response.firstName || null,
            lastName: response.lastName || null,
            email: response.email,
            role: response.role,
            status: response.status,
            permissions: (response.permissions && typeof response.permissions === 'object' && !Array.isArray(response.permissions))
              ? response.permissions as Record<string, boolean>
              : currentUser.permissions,
          });
        }
      }
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to update user';
      toast.error('Failed to update user', { description: message });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      const deletedName = selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : '';
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      toast.success('User deleted', {
        description: deletedName ? `${deletedName} has been removed.` : undefined,
      });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to delete user';
      toast.error('Failed to delete user', { description: message });
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      usersService.resetUserPassword(id, password),
    onSuccess: () => {
      setResetPasswordDialogOpen(false);
      setNewPassword('');
      toast.success('Password reset successfully', {
        description: `Password for ${selectedUser?.email} has been updated.`,
      });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to reset password';
      toast.error('Failed to reset password', { description: message });
    },
  });

  // Update form when selecting a user for editing
  useEffect(() => {
    if (selectedUser && isEditing) {
      setFormData({
        email: selectedUser.email,
        firstName: selectedUser.firstName || '',
        lastName: selectedUser.lastName || '',
        role: selectedUser.role === 'super_admin' ? 'tenant_admin' : selectedUser.role,
        phone: selectedUser.phone || '',
        jobTitle: selectedUser.jobTitle || '',
        departmentIds: selectedUser.departments?.map((d) => d.id) || [],
        status: selectedUser.status,
      });
    }
  }, [selectedUser, isEditing]);

  const handleSelectUser = (user: User) => {
    setSelectedUser(user);
    setIsCreating(false);
    setIsEditing(false);
  };

  const handleCreateNew = () => {
    setSelectedUser(null);
    setIsCreating(true);
    setIsEditing(false);
    setFormData(initialFormData);
  };

  const handleEdit = () => {
    if (selectedUser) {
      setFormData({
        email: selectedUser.email,
        firstName: selectedUser.firstName || '',
        lastName: selectedUser.lastName || '',
        role: selectedUser.role === 'super_admin' ? 'tenant_admin' : selectedUser.role,
        phone: selectedUser.phone || '',
        jobTitle: selectedUser.jobTitle || '',
        departmentIds: selectedUser.departments?.map((d) => d.id) || [],
        status: selectedUser.status,
      });
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setIsCreating(false);
    setFormData(initialFormData);
  };

  const handleSubmit = () => {
    if (isCreating) {
      createMutation.mutate({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        role: formData.role,
        phone: formData.phone || undefined,
        jobTitle: formData.jobTitle || undefined,
        departmentIds: formData.departmentIds,
      });
    } else if (isEditing && selectedUser) {
      // Only allow updating to active, inactive, or suspended (not pending)
      const validStatus = formData.status && formData.status !== 'pending'
        ? formData.status as 'active' | 'inactive' | 'suspended'
        : undefined;
      updateMutation.mutate({
        id: selectedUser.id,
        input: {
          firstName: formData.firstName || undefined,
          lastName: formData.lastName || undefined,
          role: formData.role,
          status: validStatus,
          phone: formData.phone || undefined,
          jobTitle: formData.jobTitle || undefined,
          departmentIds: formData.departmentIds,
        },
      });
    }
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedUser) {
      deleteMutation.mutate(selectedUser.id);
    }
  };

  const toggleDepartment = (deptId: string) => {
    setFormData((prev) => ({
      ...prev,
      departmentIds: prev.departmentIds?.includes(deptId)
        ? prev.departmentIds.filter((id) => id !== deptId)
        : [...(prev.departmentIds || []), deptId],
    }));
  };

  const isEmailValid = (email: string) => {
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const canSubmit = () => {
    if (isCreating) {
      return isEmailValid(formData.email) && formData.password.length >= 8;
    }
    return true;
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'super_admin':
        return <Badge variant="destructive">Super Admin</Badge>;
      case 'tenant_admin':
        return <Badge className="bg-purple-500">Admin</Badge>;
      case 'manager':
        return <Badge variant="secondary">Manager</Badge>;
      default:
        return <Badge variant="outline">User</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <Badge variant="default" className="bg-green-500">
            <CheckCircle className="mr-1 h-3 w-3" />
            Active
          </Badge>
        );
      case 'inactive':
        return (
          <Badge variant="secondary">
            <XCircle className="mr-1 h-3 w-3" />
            Inactive
          </Badge>
        );
      case 'suspended':
        return (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" />
            Suspended
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="outline">
            <AlertCircle className="mr-1 h-3 w-3" />
            Pending
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const users = data?.users || [];

  return (
    <div className="h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Users</h1>
          <p className="text-muted-foreground">
            Manage users in your organization
          </p>
        </div>
        <Button onClick={handleCreateNew}>
          <Plus className="mr-2 h-4 w-4" />
          Add User
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4 mb-4">
        <Card className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-xl font-bold">{data?.total || 0}</p>
            </div>
            <Users className="h-8 w-8 text-muted-foreground/30" />
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Active</p>
              <p className="text-xl font-bold text-green-600">
                {users.filter((u) => u.status === 'active').length}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500/30" />
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Pending</p>
              <p className="text-xl font-bold text-amber-600">
                {users.filter((u) => u.status === 'pending').length}
              </p>
            </div>
            <Clock className="h-8 w-8 text-amber-500/30" />
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Admins</p>
              <p className="text-xl font-bold text-purple-600">
                {users.filter((u) => ['super_admin', 'tenant_admin'].includes(u.role)).length}
              </p>
            </div>
            <Shield className="h-8 w-8 text-purple-500/30" />
          </div>
        </Card>
      </div>

      {/* Main Content - Master-Detail Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-10 gap-4 h-[calc(100%-10rem)]">
        {/* Left Panel - User List (40%) */}
        <Card className="lg:col-span-4 flex flex-col overflow-hidden">
          <CardHeader className="pb-3 flex-shrink-0">
            <CardTitle className="text-base">All Users</CardTitle>
            <div className="flex flex-col gap-2 pt-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
              <div className="flex gap-2">
                <Select
                  value={statusFilter || 'all'}
                  onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}
                >
                  <SelectTrigger className="h-8 text-xs flex-1">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={roleFilter || 'all'}
                  onValueChange={(v) => setRoleFilter(v === 'all' ? '' : v)}
                >
                  <SelectTrigger className="h-8 text-xs flex-1">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="tenant_admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full">
              {isLoading ? (
                <div className="space-y-2 p-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : error ? (
                <div className="py-8 text-center text-muted-foreground">
                  Failed to load users.
                </div>
              ) : users.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  <Users className="mx-auto h-12 w-12 text-muted-foreground/30 mb-4" />
                  <p>No users found.</p>
                </div>
              ) : (
                <div className="divide-y">
                  {users.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => handleSelectUser(user)}
                      className={cn(
                        'p-3 cursor-pointer hover:bg-muted/50 transition-colors',
                        selectedUser?.id === user.id && 'bg-muted'
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <UserCircle className="h-9 w-9 text-muted-foreground flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium truncate">
                              {user.firstName || user.lastName
                                ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                                : user.email.split('@')[0]}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {user.email}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          {getStatusBadge(user.status)}
                          {getRoleBadge(user.role)}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        {user.jobTitle && (
                          <span className="flex items-center gap-1 truncate">
                            <Briefcase className="h-3 w-3" />
                            {user.jobTitle}
                          </span>
                        )}
                        {user.departments && user.departments.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {user.departments.length} dept{user.departments.length > 1 ? 's' : ''}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(user.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Right Panel - Details/Edit (60%) */}
        <Card className="lg:col-span-6 flex flex-col overflow-hidden">
          {!selectedUser && !isCreating ? (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div>
                <UserCircle className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Select a User</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Choose a user from the list to view details or invite a new one.
                </p>
                <Button onClick={handleCreateNew}>
                  <Plus className="mr-2 h-4 w-4" />
                  Invite New User
                </Button>
              </div>
            </div>
          ) : isCreating ? (
            // Create New User Form
            <ScrollArea className="flex-1">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold">Create New User</h2>
                  <Button variant="ghost" size="icon" onClick={handleCancelEdit}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <UserCircle className="h-4 w-4" />
                      Basic Information
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="user@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className={
                          formData.email && !isEmailValid(formData.email)
                            ? 'border-destructive'
                            : ''
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password">
                        Password *
                      </Label>
                      <Input
                        id="password"
                        type="password"
                        placeholder="Minimum 8 characters"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className={
                          formData.password && formData.password.length < 8
                            ? 'border-destructive'
                            : ''
                        }
                      />
                      {formData.password && formData.password.length < 8 && (
                        <p className="text-xs text-destructive">Password must be at least 8 characters</p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name</Label>
                        <Input
                          id="firstName"
                          placeholder="John"
                          value={formData.firstName}
                          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name</Label>
                        <Input
                          id="lastName"
                          placeholder="Doe"
                          value={formData.lastName}
                          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Role & Contact */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Shield className="h-4 w-4" />
                      Role & Contact
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="role">Role</Label>
                      <Select
                        value={formData.role}
                        onValueChange={(v) => setFormData({ ...formData, role: v as any })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="tenant_admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          placeholder="+1 234 567 890"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="jobTitle">Job Title</Label>
                        <Input
                          id="jobTitle"
                          placeholder="Software Engineer"
                          value={formData.jobTitle}
                          onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Departments */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Building2 className="h-4 w-4" />
                      Departments
                    </div>

                    <div className="border rounded-md p-3 space-y-2 max-h-40 overflow-y-auto">
                      {departmentsData?.departments.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No departments available</p>
                      ) : (
                        departmentsData?.departments.map((dept) => (
                          <div key={dept.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`dept-${dept.id}`}
                              checked={formData.departmentIds?.includes(dept.id)}
                              onCheckedChange={() => toggleDepartment(dept.id)}
                            />
                            <label
                              htmlFor={`dept-${dept.id}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              {dept.name}
                              {dept.code && (
                                <span className="text-muted-foreground ml-1">({dept.code})</span>
                              )}
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                  <Button variant="outline" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={!canSubmit() || createMutation.isPending}
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create User'}
                  </Button>
                </div>
              </div>
            </ScrollArea>
          ) : selectedUser && isEditing ? (
            // Edit User Form
            <ScrollArea className="flex-1">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold">Edit User</h2>
                  <Button variant="ghost" size="icon" onClick={handleCancelEdit}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <UserCircle className="h-4 w-4" />
                      Basic Information
                    </div>

                    <div className="space-y-2">
                      <Label>Email Address</Label>
                      <Input
                        value={selectedUser.email}
                        disabled
                        className="bg-muted"
                      />
                      <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-firstName">First Name</Label>
                        <Input
                          id="edit-firstName"
                          value={formData.firstName}
                          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-lastName">Last Name</Label>
                        <Input
                          id="edit-lastName"
                          value={formData.lastName}
                          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Role & Status */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Shield className="h-4 w-4" />
                      Role & Status
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-role">Role</Label>
                        <Select
                          value={formData.role}
                          onValueChange={(v) => setFormData({ ...formData, role: v as any })}
                          disabled={selectedUser.role === 'super_admin'}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="tenant_admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-status">Status</Label>
                        <Select
                          value={formData.status}
                          onValueChange={(v) => setFormData({ ...formData, status: v as any })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                            <SelectItem value="suspended">Suspended</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-phone">Phone</Label>
                        <Input
                          id="edit-phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-jobTitle">Job Title</Label>
                        <Input
                          id="edit-jobTitle"
                          value={formData.jobTitle}
                          onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Departments */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Building2 className="h-4 w-4" />
                      Departments
                    </div>

                    <div className="border rounded-md p-3 space-y-2 max-h-40 overflow-y-auto">
                      {departmentsData?.departments.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No departments available</p>
                      ) : (
                        departmentsData?.departments.map((dept) => (
                          <div key={dept.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`edit-dept-${dept.id}`}
                              checked={formData.departmentIds?.includes(dept.id)}
                              onCheckedChange={() => toggleDepartment(dept.id)}
                            />
                            <label
                              htmlFor={`edit-dept-${dept.id}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              {dept.name}
                              {dept.code && (
                                <span className="text-muted-foreground ml-1">({dept.code})</span>
                              )}
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                  <Button variant="outline" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={updateMutation.isPending}
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </ScrollArea>
          ) : selectedUser ? (
            // View User Details
            <Tabs defaultValue="overview" className="flex-1 flex flex-col">
              <div className="p-4 border-b flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <UserCircle className="h-12 w-12 text-muted-foreground" />
                    <div>
                      <h2 className="text-xl font-semibold">
                        {selectedUser.firstName || selectedUser.lastName
                          ? `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim()
                          : selectedUser.email.split('@')[0]}
                      </h2>
                      <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleEdit}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={handleDelete}
                      disabled={selectedUser.role === 'super_admin'}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  {getStatusBadge(selectedUser.status)}
                  {getRoleBadge(selectedUser.role)}
                </div>
              </div>

              <TabsList className="mx-4 mt-2 w-fit">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="departments">Departments</TabsTrigger>
                <TabsTrigger value="activity">Activity</TabsTrigger>
              </TabsList>

              <ScrollArea className="flex-1">
                <TabsContent value="overview" className="p-4 m-0">
                  {/* Quick Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <Card className="p-3">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Role</span>
                      </div>
                      <div className="mt-1">{getRoleBadge(selectedUser.role)}</div>
                    </Card>
                    <Card className="p-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Status</span>
                      </div>
                      <div className="mt-1">{getStatusBadge(selectedUser.status)}</div>
                    </Card>
                    <Card className="p-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Departments</span>
                      </div>
                      <p className="text-xl font-bold mt-1">{selectedUser.departments?.length || 0}</p>
                    </Card>
                    <Card className="p-3">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Last Login</span>
                      </div>
                      <p className="text-sm font-medium mt-1">
                        {selectedUser.lastLoginAt
                          ? formatDistanceToNow(new Date(selectedUser.lastLoginAt), { addSuffix: true })
                          : 'Never'}
                      </p>
                    </Card>
                  </div>

                  {/* Details */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">User Information</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground flex items-center gap-1">
                            <Mail className="h-3 w-3" /> Email
                          </p>
                          <p className="font-medium mt-1">{selectedUser.email}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground flex items-center gap-1">
                            <Phone className="h-3 w-3" /> Phone
                          </p>
                          <p className="font-medium mt-1">{selectedUser.phone || '-'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground flex items-center gap-1">
                            <Briefcase className="h-3 w-3" /> Job Title
                          </p>
                          <p className="font-medium mt-1">{selectedUser.jobTitle || '-'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> Joined
                          </p>
                          <p className="font-medium mt-1">
                            {new Date(selectedUser.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </p>
                        </div>
                        {selectedUser.invitedAt && (
                          <div>
                            <p className="text-muted-foreground">Invited At</p>
                            <p className="font-medium mt-1">
                              {new Date(selectedUser.invitedAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </p>
                          </div>
                        )}
                        <div>
                          <p className="text-muted-foreground">User ID</p>
                          <p className="font-mono text-xs mt-1">{selectedUser.id}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Actions */}
                  <Card className="mt-4">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Quick Actions</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setResetPasswordDialogOpen(true)}
                        >
                          <KeyRound className="mr-2 h-4 w-4" />
                          Reset Password
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="departments" className="p-4 m-0">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Department Assignments
                      </CardTitle>
                      <CardDescription>
                        Departments this user belongs to
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {selectedUser.departments && selectedUser.departments.length > 0 ? (
                        <div className="space-y-2">
                          {selectedUser.departments.map((dept) => (
                            <div
                              key={dept.id}
                              className="flex items-center justify-between p-3 border rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                <Building2 className="h-5 w-5 text-muted-foreground" />
                                <div>
                                  <p className="font-medium">{dept.name}</p>
                                  <p className="text-xs text-muted-foreground capitalize">
                                    {dept.departmentRole}
                                  </p>
                                </div>
                              </div>
                              <Badge variant="outline">{dept.departmentRole}</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <Building2 className="h-12 w-12 mx-auto mb-4 opacity-30" />
                          <p>No department assignments</p>
                          <p className="text-sm">Click Edit to assign departments.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="activity" className="p-4 m-0">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Activity Log
                      </CardTitle>
                      <CardDescription>
                        Recent activity for this user
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-center py-8 text-muted-foreground">
                        <Clock className="h-12 w-12 mx-auto mb-4 opacity-30" />
                        <p>Activity logging coming soon.</p>
                        <p className="text-sm">Track user logins, actions, and changes.</p>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          ) : null}
        </Card>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <strong>
                {selectedUser?.firstName} {selectedUser?.lastName}
              </strong>{' '}
              ({selectedUser?.email})? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog */}
      <AlertDialog open={resetPasswordDialogOpen} onOpenChange={(open) => {
        setResetPasswordDialogOpen(open);
        if (!open) setNewPassword('');
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Password</AlertDialogTitle>
            <AlertDialogDescription>
              Set a new password for <strong>{selectedUser?.email}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              id="newPassword"
              type="password"
              placeholder="Minimum 8 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={newPassword && newPassword.length < 8 ? 'border-destructive' : ''}
            />
            {newPassword && newPassword.length < 8 && (
              <p className="text-xs text-destructive mt-1">Password must be at least 8 characters</p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!newPassword || newPassword.length < 8 || resetPasswordMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (selectedUser && newPassword.length >= 8) {
                  resetPasswordMutation.mutate({ id: selectedUser.id, password: newPassword });
                }
              }}
            >
              {resetPasswordMutation.isPending ? 'Resetting...' : 'Reset Password'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
